import React                                  from "react"
import { useLocation }                        from "react-router-dom"
import { useGlobalConfig }                    from "context/GlobalContext"
import { Box, 
         Collapse, 
         List, 
         ListItem, 
         ListItemButton, 
         ListItemIcon, 
         ListItemText 
       }                                      from "@mui/material"
import { Home as LucideHome, ShieldCheck as LucideShieldCheck, ChevronDown as LucideChevronDown } from 'lucide-react';
import { ROUTE_TREE }                         from "routes/AppRoutes";
import PollOutlinedIcon   from '@mui/icons-material/PollOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import AreaChartOutlinedIcon from '@mui/icons-material/AreaChartOutlined';
import { House } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { ChartArea } from 'lucide-react';
import { ChartColumn } from 'lucide-react';

export type UtilityDrawerProps = {
  isExpanded : boolean,
}

export const UtilityDrawer = ({
    isExpanded
  } : UtilityDrawerProps) => {

  const { rc } = useGlobalConfig()
  const location = useLocation()

  const [adminOpen, setAdminOpen] = React.useState<boolean>(false)

  const navToUtilityList = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.LIST, {
      disableBack : true
    })
  }

  const navToWorkflows = () => {
    rc.uiRouter.navigate(ROUTE_TREE.WORKFLOW.LIST)
  }

  const navToEnvironments = () => {
    rc.uiRouter.navigate(ROUTE_TREE.ENVIRONMENT.LIST)
  }

  const navToApplicationScopeMaster = () => {
    rc.uiRouter.navigate(ROUTE_TREE.APPLICATION_SCOPE_MASTER.LIST)
  }

  const navToPipelineMaster = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PIPELINE_MASTER.LIST)
  }

  const navToGlobalDataScenarios = () => {
    rc.uiRouter.navigate(ROUTE_TREE.GLOBAL_DATA_SCENARIOS.LAYOUT)
  }

  const isActive = (pathPrefix: string) => location.pathname.startsWith(pathPrefix)

  React.useEffect(() => {
    if (isActive('/workflow') || isActive('/environment') || isActive('/application-scope-master') || isActive('/pipeline-master') || isActive('/global-data-scenarios')) {
      setAdminOpen(true)
    }
  }, [location.pathname])

  return (
    <React.Fragment>
      <ListItem disablePadding>
        <ListItemButton 
          onClick={navToUtilityList} 
          selected={isActive('/project/list')}
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : {}}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
            <House style={{ color: '#bfccd9' }} size={16} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="Projects" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
        </ListItemButton>
      </ListItem>

      {/* Administration group as accordion header */}
      <ListItem disablePadding>
        <ListItemButton 
          onClick={() => setAdminOpen(v => !v)} 
          selected={adminOpen || isActive('/workflow') || isActive('/environment') || isActive('/application-scope-master') || isActive('/global-data-scenarios')} 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
            <ShieldCheck style={{ color: '#bfccd9' }} size={18} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="Administration" primaryTypographyProps={{ sx: { color: 'inherit', fontWeight: 600, fontSize: 14 } }} />}
          {isExpanded && (
            <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={adminOpen && isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
          <List component="div" disablePadding>
            <ListItemButton 
              dense 
              sx={{ 
                py: 0.75, 
                color: 'text.primary', 
                '&:hover': { backgroundColor: 'action.hover' },
                '&.Mui-selected': { 
                  color: '#e8eaf6',
                }
              }} 
              onClick={navToWorkflows} 
              selected={isActive('/workflow')}
            >
              <ListItemText 
                primary="Workflows" 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive('/workflow') ? 600 : 500,
                    color: 'inherit'
                  } 
                }} 
              />
            </ListItemButton>
            <ListItemButton 
              dense 
              sx={{ 
                py: 0.75, 
                color: 'text.primary', 
                '&:hover': { backgroundColor: 'action.hover' },
                '&.Mui-selected': { 
                  color: '#e8eaf6',
                }
              }} 
              onClick={navToEnvironments} 
              selected={isActive('/environment')}
            >
              <ListItemText 
                primary="Environments" 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive('/environment') ? 600 : 500,
                    color: 'inherit'
                  } 
                }} 
              />
            </ListItemButton>
            <ListItemButton 
              dense 
              sx={{ 
                py: 0.75, 
                color: 'text.primary', 
                '&:hover': { backgroundColor: 'action.hover' },
                '&.Mui-selected': { 
                  color: '#e8eaf6',
                }
              }} 
              onClick={navToApplicationScopeMaster} 
              selected={isActive('/application-scope-master')}
            >
              <ListItemText 
                primary="Application Scope" 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive('/application-scope-master') ? 600 : 500,
                    color: 'inherit'
                  } 
                }} 
              />
            </ListItemButton>
            <ListItemButton 
              dense 
              sx={{ 
                py: 0.75, 
                color: 'text.primary', 
                '&:hover': { backgroundColor: 'action.hover' },
                '&.Mui-selected': { 
                  color: '#e8eaf6',
                }
              }} 
              onClick={navToGlobalDataScenarios} 
              selected={isActive('/global-data-scenarios')}
            >
              <ListItemText 
                primary="Global Data Scenarios" 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive('/global-data-scenarios') ? 600 : 500,
                    color: 'inherit'
                  } 
                }} 
              />
            </ListItemButton>
            <ListItemButton 
              dense 
              sx={{ 
                py: 0.75, 
                color: 'text.primary', 
                '&:hover': { backgroundColor: 'action.hover' },
                '&.Mui-selected': { 
                  color: '#e8eaf6',
                }
              }} 
              onClick={navToPipelineMaster} 
              selected={isActive('/pipeline-master')}
            >
              <ListItemText 
                primary="Pipeline Master" 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive('/pipeline-master') ? 600 : 500,
                    color: 'inherit'
                  } 
                }} 
              />
            </ListItemButton>
          </List>
        </Box>
      </Collapse>

      <ListItem disablePadding>
        <ListItemButton 
          onClick={navToWorkflows} 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto', color: '#bfccd9' }}>
            <ChartArea style={{ color: '#bfccd9' }} size={18} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="System Monitoring" primaryTypographyProps={{ sx: { color: '#bfccd9',fontSize: '14px' } }} />}
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' ,color: '#bfccd9' }}>
            <ChartColumn style={{ color: '#bfccd9' }} size={18} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="System KPIs" primaryTypographyProps={{ sx: { color: '#bfccd9',fontSize: '14px'} }} />}
        </ListItemButton>
      </ListItem>

    </React.Fragment>
  )
}